import * as React from "react";
import {
  MultiSelect,
  ItemRenderer,
  ItemRendererProps,
} from "@blueprintjs/select";
import { MenuItem, MenuItemProps } from "@blueprintjs/core";
import { useCallback } from "react";
import { IUser, IGroup } from "../../../api/admin";
import { useSelectedItems } from "../../../hooks/useSelectedItems";
import { cappedItemListRenderer } from "./cappedItemListRenderer";

const getGroupDisplayLabel = (group: IGroup): string => {
  if (group.autogenerated && group.folder_label && group.permission_label) {
    return `${group.folder_label} - ${group.permission_label}`;
  }
  return group.name;
};

const filterGroup = (query: string, group: IGroup) => {
  const displayLabel = getGroupDisplayLabel(group);
  const searchText = `${displayLabel} ${group.name}`.toLowerCase();
  return searchText.includes(query.toLowerCase());
};

const areGroupsEqual = (group1: IGroup, group2: IGroup) =>
  group1.id === group2.id;

const renderTag = (group: IGroup) => {
  return getGroupDisplayLabel(group);
};

const isFolderGroup = (group: IGroup) => group.autogenerated === true;

interface GroupMultiSelectProps {
  user: IUser;
  groups: IGroup[];
  onGroupChange: (userId: number, group_ids: number[]) => void;
  showOnlyFolderGroups?: boolean;
}

export function GroupMultiSelect({
  user,
  groups,
  onGroupChange,
  showOnlyFolderGroups = false,
}: GroupMultiSelectProps) {
  const displayGroups = React.useMemo(
    () => (showOnlyFolderGroups ? groups.filter(isFolderGroup) : groups),
    [groups, showOnlyFolderGroups]
  );

  const { idSet: selectedGroupIdSet, selectedItems } = useSelectedItems(
    user.group_ids,
    groups
  );

  const handleGroupRemove = useCallback(
    (group: IGroup) => {
      const currentGroupIds = user.group_ids || [];
      const newGroupIds = currentGroupIds.filter(
        (id: number) => id !== group.id
      );
      onGroupChange(user.id, newGroupIds);
    },
    [user, onGroupChange]
  );

  const handleGroupSelect = useCallback(
    (group: IGroup) => {
      const currentGroupIds = user.group_ids || [];
      if (selectedGroupIdSet.has(group.id)) {
        handleGroupRemove(group);
      } else {
        const newGroupIds = [...currentGroupIds, group.id];
        onGroupChange(user.id, newGroupIds);
      }
    },
    [user, selectedGroupIdSet, onGroupChange, handleGroupRemove]
  );

  const handleGroupClear = useCallback(() => {
    onGroupChange(user.id, []);
  }, [user, onGroupChange]);

  const renderGroup: ItemRenderer<IGroup> = useCallback(
    (group, rendererProps) => {
      if (!rendererProps.modifiers.matchesPredicate) {
        return null;
      }
      const isSelected = selectedGroupIdSet.has(group.id);

      return (
        <MenuItem
          {...getGroupItemProps(group, rendererProps, isSelected)}
          roleStructure="listoption"
          shouldDismissPopover={false}
          text={getGroupDisplayLabel(group)}
        />
      );
    },
    [selectedGroupIdSet]
  );

  return (
    <MultiSelect
      items={displayGroups}
      selectedItems={selectedItems}
      itemRenderer={renderGroup}
      itemListRenderer={cappedItemListRenderer}
      itemPredicate={filterGroup}
      itemsEqual={areGroupsEqual}
      onItemSelect={handleGroupSelect}
      onRemove={handleGroupRemove}
      onClear={handleGroupClear}
      placeholder={
        showOnlyFolderGroups ? "Select folders..." : "Select access..."
      }
      disabled={!displayGroups || displayGroups.length === 0}
      popoverProps={{
        minimal: true,
        matchTargetWidth: true,
      }}
      tagRenderer={renderTag}
      tagInputProps={{
        tagProps: {
          minimal: false,
        },
      }}
      resetOnSelect={true}
    />
  );
}

export function getGroupItemProps(
  group: IGroup,
  { handleClick, handleFocus, modifiers, query, ref }: ItemRendererProps,
  isSelected?: boolean
): MenuItemProps & React.Attributes {
  return {
    active: modifiers.active,
    disabled: modifiers.disabled,
    key: group.id,
    onClick: handleClick,
    onFocus: handleFocus,
    ref,
    text: getGroupDisplayLabel(group),
    selected: isSelected,
  };
}
